import FetchFacilityUsers from '@Application/Users/FacilityUsers/Queries/FetchFacilityUsers';
import fetchFacilityUsers from '@Application/Users/FacilityUsers/fetchFacilityUsers';
import AgencyScopeFilter from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/AgencyScopeFilter';
import ArchivedFilter from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/ArchivedFilter';
import AssignedUserFilter, {
	type AssignedUserOption,
} from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/AssignedUserFilter';
import BeneficiaryFilterView from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/BeneficiaryFilterView';
import buildAgencyScopeOptions from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Helpers/buildAgencyScopeOptions';
import countAssignedFacilities from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Helpers/countAssignedFacilities';
import resolveAgencyScopeKey from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Helpers/resolveAgencyScopeKey';
import SearchFilter from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/SearchFilter';
import beneficiaryFiltersSignal, {
	setBeneficiaryFilters,
} from '@UI/Pages/Beneficiaries/BeneficiaryList/Filters/Signals/beneficiaryFiltersSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const MY_BENEFICIARIES_OPTION_LABEL = 'Mes dossiers';
const DEACTIVATED_ACCOUNT_LABEL_SUFFIX = ' (compte désactivé)';

/**
 * Lays out the filter fields; each field reads and writes `beneficiaryFiltersSignal` itself
 * (`guides/lists-and-filters.md`). The agency choices and the referent directory are loaded here, so
 * opening the panel never refetches them. The directory endpoint covers one agency only: it is read
 * for the chosen agency, or the caller's only one, and not at all when several are in play.
 */
const Filters: React.FC = () => {
	const dispatch = useDispatch();
	const currentFilters = useSignalValue(beneficiaryFiltersSignal);
	const { user } = useStrictAuthenticatedUser();
	const currentUserId = user.id;
	const { organizations } = useCurrentOrganization();
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();

	const agencyScopeOptions = React.useMemo(() => buildAgencyScopeOptions(organizations), [organizations]);
	const assignedFacilityCount = React.useMemo(
		() => countAssignedFacilities(organizations),
		[organizations],
	);
	const agencyScopeKey = resolveAgencyScopeKey(currentFilters.agencyScope);
	const isAgencyScopeStale = !agencyScopeOptions.some((option) => option.key === agencyScopeKey);
	const isAgencyScopeVisible = assignedFacilityCount > 1;

	// The signal outlives an unassignment or a re-login: a scope that is no longer offered falls back
	// to "all". It settles in one write, since "all" is always among the options.
	React.useEffect(() => {
		if (isAgencyScopeStale) {
			setBeneficiaryFilters(beneficiaryFiltersSignal.peek().setAgencyScope({ type: 'all' }, []));
		}
	}, [isAgencyScopeStale]);

	const effectiveFacilityId = React.useMemo(() => {
		const { agencyScope } = currentFilters;

		if (agencyScope.type === 'facility') {
			return agencyScope.facilityId;
		}

		if (assignedFacilityCount === 1) {
			return organizations.flatMap((organization) => organization.facilities)[0]?.id ?? null;
		}

		return null;
	}, [currentFilters, assignedFacilityCount, organizations]);

	// A deactivated professional can remain the referent of existing dossiers: the query never filters
	// on `isActive`, and this filter — a read, not an assignment — must keep them selectable, only
	// flagged in their label.
	const { data: facilityUsers, error: facilityUsersError } = useStoreQuery({
		selector: (state) => state.facilityUsersStore.users,
		stopOn: (value) => value !== null && value.facilityId === effectiveFacilityId,
		query: () => {
			if (effectiveFacilityId === null) {
				return;
			}

			return dispatch(fetchFacilityUsers(new FetchFacilityUsers(effectiveFacilityId)));
		},
		deps: [effectiveFacilityId],
	});

	const assignedUserOptions = React.useMemo<readonly AssignedUserOption[]>(() => {
		const scopeOptions: AssignedUserOption[] = [
			{ id: currentUserId, label: MY_BENEFICIARIES_OPTION_LABEL },
		];

		// The store may hold another agency's directory: it is only read for the effective agency.
		if (effectiveFacilityId === null || facilityUsers === null) {
			return scopeOptions;
		}

		const directoryOptions = facilityUsers.list
			.filter((facilityUser) => facilityUser.id !== currentUserId)
			.map((facilityUser) => ({
				id: facilityUser.id,
				label: facilityUser.isActive
					? facilityUser.fullName
					: `${facilityUser.fullName}${DEACTIVATED_ACCOUNT_LABEL_SUFFIX}`,
			}));

		return [...scopeOptions, ...directoryOptions];
	}, [currentUserId, effectiveFacilityId, facilityUsers]);

	const handleReset = React.useCallback(() => {
		setBeneficiaryFilters(BeneficiaryFilterView.createDefault());
	}, []);

	const isAssignedUserLoading =
		effectiveFacilityId !== null && facilityUsers === null && facilityUsersError === null;

	return (
		<React.Fragment>
			<Grid columns={10} columnGap={1} rowGap={1} alignItems="end">
				<GridItem columnSize={{ xl: 4, lg: 4, md: 8, sm: 7, xs: 6 }}>
					<SearchFilter />
				</GridItem>
				<ConditionalLoader
					data={!isCompact}
					fallback={
						<GridItem columnSize={{ md: 2, sm: 3, xs: 4 }}>
							<FilterPanelTrigger
								panelId={panelId}
								isOpen={isOpen}
								activeCount={currentFilters.activeFilterCount}
								onClick={open}
							/>
						</GridItem>
					}
				>
					<ConditionalLoader data={isAgencyScopeVisible}>
						<GridItem columnSize={{ xl: 2, lg: 2, md: 2 }}>
							<AgencyScopeFilter options={agencyScopeOptions} currentUserId={currentUserId} />
						</GridItem>
					</ConditionalLoader>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 2 }}>
						<AssignedUserFilter options={assignedUserOptions} loading={isAssignedUserLoading} />
					</GridItem>
					<GridItem columnSize={{ xl: 2, lg: 2, md: 2 }} alignSelf="center">
						<ArchivedFilter />
					</GridItem>
				</ConditionalLoader>
			</Grid>
			<FilterPanelSidebar
				panelId={panelId}
				visible={isOpen}
				activeCount={currentFilters.activeFilterCount}
				onHide={close}
				onReset={handleReset}
			>
				<Grid columns={1} rowGap={1}>
					<ConditionalLoader data={isAgencyScopeVisible}>
						<GridItem>
							<AgencyScopeFilter
								options={agencyScopeOptions}
								currentUserId={currentUserId}
								withLabel
							/>
						</GridItem>
					</ConditionalLoader>
					<GridItem>
						<AssignedUserFilter
							options={assignedUserOptions}
							loading={isAssignedUserLoading}
							withLabel
						/>
					</GridItem>
					<GridItem>
						<ArchivedFilter />
					</GridItem>
				</Grid>
			</FilterPanelSidebar>
		</React.Fragment>
	);
};

export default Filters;
